/**
 * DentalAgeCard — Phase C Section 8 — educational Dental Age™ metric.
 *
 * Adds/subtracts years to chronological age based on lifestyle + metabolic
 * drivers. Shows confidence and contributing factors transparently.
 *
 * STRICTLY EDUCATIONAL. Not a tooth-tissue measurement.
 */
import React from "react";
import { Hourglass, AlertCircle } from "lucide-react";
import ScoreMethodology, { BandLine } from "@/components/dental_intel/ScoreMethodology";
import ScoreForecast from "@/components/dental_intel/ScoreForecast";
// iter1111 — delegate to factor-summary card when backend retired the numeric age.
import { DentalAgeFactorCard } from "./DhiRetiredCards";

const CONFIDENCE_LABEL = {
  bg: { high: "Висока", medium: "Средна", low: "Ниска" },
  en: { high: "High",   medium: "Medium", low: "Low" },
  es: { high: "Alto", medium: "Medio", low: "Bajo" },
};

export default function DentalAgeCard({ data, forecast, isDark, lang = "bg", cardCls }) {
  // iter1111 — The DHI evidence audit retired the numeric Dental Age
  // because it had no validated derivation and the `max(15, ...)` floor
  // bug returned 15 for a 5-year-old. When the backend emits the retired
  // factor_summary shape, render it via the new factor card instead of
  // crashing with `NaN` on `data.dental_age`.
  if (data && (data._retired || data.status === "factor_summary"
               || data.status === "retired_unvalidated"
               || data.status === "not_assessable")) {
    return <DentalAgeFactorCard data={data} lang={lang} isDark={isDark} />;
  }
  const t = {
    bg: {
      title: "Дентална възраст",
      chrono: "Хронологична възраст",
      dental: "Дентална възраст",
      delta: "Разлика",
      drivers: "Фактори",
      confidence: "Увереност",
      missing: "Възрастта липсва — добавете в профила за изчисление.",
      whatIsIt: "Какво е Дентална възраст?",
      whatIsItDesc: "Образователен индекс, който показва биологичната възраст на устната ви кухина — не на самите зъби — спрямо хронологичната. Доказани метаболитни и лайфстайл фактори (диабет, пушене, инфламация, хранене) ускоряват или забавят оралното биостареене.",
      howWorks: "Как работи изчислението?",
      howWorksDesc: "Започваме от хронологичната ви възраст и добавяме/изваждаме години според вашите модифицируеми фактори: пушене (+6 год.), диабет (+5), затлъстяване (+3), високи захари (+3), хронично възпаление (+2) — и обратно: омега-3 ≥ 8% (−2), Vit D ≥ 40 (−2), високи фибри (−1), здравословен начин на живот (−2).",
      olderMeans: "По-висока дентална възраст означава",
      olderMeansList: [
        "По-натоварена среда за зъбите и венците",
        "По-висок риск от ерозия, кариес и рецесия на венците",
        "По-голяма нужда от превантивни интервенции",
      ],
      youngerMeans: "По-ниска дентална възраст означава",
      youngerMeansList: [
        "Защитена среда, ниска инфламация",
        "Добър реминерализационен потенциал",
        "Поддържайте текущите си навици",
      ],
      improve: "Как да намалите вашата Дентална възраст?",
      improveList: [
        "Спрете пушенето — най-силният единичен лост (−6 г.)",
        "Стабилизирайте HbA1c < 5.7% (−5 г.)",
        "Омега-3 индекс ≥ 8% (риба 2-3×/седм. или EPA+DHA 1-2 g/ден)",
        "Витамин D ≥ 40 ng/ml — 10-20 мин слънце или D3 1000-2000 IU/ден",
        "Намалете добавени захари под 25 g/ден (WHO)",
        "Поддържайте BMI 20-25 + 150 мин/седм. умерено аеробно",
      ],
    },
    en: {
      title: "Dental Age",
      chrono: "Chronological age",
      dental: "Dental Age",
      delta: "Delta",
      drivers: "Drivers",
      confidence: "Confidence",
      missing: "Age missing — add it in your profile to compute.",
      whatIsIt: "What is Dental Age?",
      whatIsItDesc: "An educational index showing the biological age of your oral environment — not the teeth themselves — vs. chronological age. Established metabolic and lifestyle factors (diabetes, smoking, inflammation, nutrition) accelerate or slow oral biological aging.",
      howWorks: "How is it calculated?",
      howWorksDesc: "We start from your chronological age and add/subtract years from modifiable factors: smoking (+6y), diabetes (+5), obesity (+3), high sugar (+3), chronic inflammation (+2). Subtractions: omega-3 ≥ 8% (−2), Vit D ≥ 40 (−2), high fiber (−1), healthy lifestyle (−2).",
      olderMeans: "A higher Dental Age means",
      olderMeansList: [
        "More stressed environment for teeth and gums",
        "Higher risk of erosion, caries, gum recession",
        "Greater need for preventive interventions",
      ],
      youngerMeans: "A lower Dental Age means",
      youngerMeansList: [
        "Protected environment, low inflammation",
        "Good remineralization potential",
        "Maintain your current habits",
      ],
      improve: "How to lower your Dental Age?",
      improveList: [
        "Quit smoking — strongest single lever (−6y)",
        "Stabilize HbA1c < 5.7% (−5y)",
        "Omega-3 index ≥ 8% (fish 2-3×/wk or EPA+DHA 1-2 g/d)",
        "Vitamin D ≥ 40 ng/ml — 10-20 min sun or D3 1000-2000 IU/d",
        "Reduce added sugar < 25 g/d (WHO)",
        "Maintain BMI 20-25 + 150 min/wk moderate aerobic",
      ],
    },
  }[lang] || {};

  if (!data || data.chronological == null) {
    return (
      <div className={`${cardCls} p-5`} data-testid="dhi-dental-age-card">
        <div className="flex items-center gap-2 mb-2">
          <Hourglass className="w-5 h-5 text-cyan-500" />
          <h3 className="text-lg font-bold">{t.title}</h3>
        </div>
        <p className="text-sm opacity-60">{t.missing}</p>
      </div>
    );
  }

  const delta = data.delta || 0;
  const isOlder = delta > 0;
  const isYounger = delta < 0;
  const ageBand = delta <= -2 ? "green" : delta <= 2 ? "yellow" : delta <= 5 ? "orange" : "red";
  const ageColor = isOlder ? "text-rose-500" : isYounger ? "text-emerald-500" : "text-gray-500";

  return (
    <div className={`${cardCls} p-5`} data-testid="dhi-dental-age-card">
      <div className="flex items-center gap-2 mb-1">
        <Hourglass className="w-5 h-5 text-cyan-500" />
        <h3 className="text-lg font-bold">{t.title}</h3>
      </div>
      <p className="text-xs opacity-60 mb-3">
        {lang === "en" ? data.note_en : data.note_bg}
      </p>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat label={t.chrono} value={data.chronological} testid="dhi-age-chrono" />
        <Stat
          label={t.dental}
          value={data.dental_age}
          highlight={ageColor}
          testid="dhi-age-dental"
        />
        <Stat
          label={t.delta}
          value={delta > 0 ? `+${delta}y` : `${delta}y`}
          highlight={ageColor}
          testid="dhi-age-delta"
        />
      </div>

      <BandLine explain={data?.explain} band={ageBand} testid="dhi-age-band" />

      <div className="mb-3">
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="font-bold opacity-70">{t.drivers}</span>
          <span className="opacity-60">
            {t.confidence}: <strong>{(CONFIDENCE_LABEL[lang] || CONFIDENCE_LABEL.bg)[data.confidence]}</strong>
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(data.drivers || []).map((d, i) => {
            const pos = d.years > 0;
            return (
              <span
                key={i}
                className={`text-[10px] px-2 py-1 rounded-full font-mono ${
                  pos
                    ? (isDark ? "bg-rose-500/20 text-rose-300" : "bg-rose-50 text-rose-700")
                    : (isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-50 text-emerald-700")
                }`}
              >
                {lang === "en" ? d.label_en : d.label_bg} {pos ? "+" : ""}{d.years}y
              </span>
            );
          })}
        </div>
      </div>

      <div className={`text-[10px] flex items-start gap-1.5 opacity-60 mb-4`}>
        <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
        {lang === "en"
          ? "Educational only. Dental Age is a lifestyle-weighted aggregate, not a clinical tooth measurement."
          : "Само образователно. Денталната възраст е претеглена агрегация на начина на живот, не клинично измерване на зъбите."}
      </div>

      {/* Единна методика + прогноза (iter1028) */}
      <div className="space-y-3 pt-3 border-t border-black/5 dark:border-white/5">
        <details className="group" data-testid="dhi-age-explanation">
          <summary className={`cursor-pointer text-sm font-bold flex items-center gap-2 ${isDark ? "text-cyan-200" : "text-cyan-700"}`}>
            <span className="opacity-50 group-open:rotate-90 transition-transform inline-block">▶</span>
            {t.whatIsIt}
          </summary>
          <p className="text-xs opacity-80 leading-relaxed mt-2 pl-5">{t.whatIsItDesc}</p>
        </details>

        <ScoreMethodology
          explain={data?.explain}
          contributors={data?.contributors}
          isDark={isDark}
          unit="години"
          barScale={12}
          title="Как точно се изчислява и кога е висока"
          testid="dhi-age-method"
        />

        <ScoreForecast forecast={forecast} metric="years" isDark={isDark}
          testid="dhi-age-forecast" />
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, testid }) {
  return (
    <div
      className={`rounded-xl p-3 text-center bg-gradient-to-br from-cyan-500/10 to-sky-500/10 border border-cyan-200/30 dark:border-cyan-500/20`}
      data-testid={testid}
    >
      <div className="text-[10px] opacity-60 mb-0.5">{label}</div>
      <div className={`text-2xl font-extrabold ${highlight || ""}`}>{value}</div>
    </div>
  );
}
